/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * HeroEditor — full-featured Hero Section configuration panel.
 * 10 collapsible accordion sections covering every visual aspect of a hero.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import { usePages } from '../../../hooks/usePages';

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_HERO_CONFIG = {
  // Layout
  layoutType: 'centered',
  sectionHeight: 'auto',
  sectionHeightPx: 600,
  contentMaxWidth: 'medium',
  paddingTop: 80,
  paddingBottom: 80,
  textAlignment: 'center',

  // Background
  bgType: 'gradient',
  bgColor: '#1e293b',
  bgGradientFrom: '#6366f1',
  bgGradientTo: '#0ea5e9',
  bgGradientType: 'linear',
  bgGradientAngle: 135,
  bgImageUrl: '',
  bgImagePosition: 'center',
  bgImageSize: 'cover',
  bgOverlayColor: '#000000',
  bgOverlayOpacity: 40,
  bgParallax: false,

  // Badge
  badgeVisible: false,
  badgeText: 'New Feature',
  badgeColor: '#7c3aed',
  badgeStyle: 'pill',
  badgeLink: '',

  // Headline
  headlineText: 'Build something great',
  headlineFontSize: 'xl',
  headlineColor: '#ffffff',
  headlineHighlightWord: '',
  headlineHighlightStyle: 'color',
  headlineHighlightColor: '#f59e0b',
  headlineTypewriter: false,
  headlineTypewriterWords: [],

  // Subheadline
  subheadlineVisible: true,
  subheadlineText: 'Everything you need to launch faster.',
  subheadlineFontSize: 'md',
  subheadlineColor: '#cbd5e1',

  // CTA
  primaryCTA: {
    label: 'Get Started',
    linkType: 'url',
    url: '/sign-up',
    targetSectionId: '',
    targetPageId: '',
    targetPageSlug: '',
    icon: '',
    iconPosition: 'left',
    openNewTab: false,
    style: 'filled',
    bgColor: '',
    textColor: '',
  },
  secondaryCTA: {
    visible: false,
    label: 'Learn More',
    linkType: 'url',
    url: '#',
    targetSectionId: '',
    targetPageId: '',
    targetPageSlug: '',
    icon: '',
    style: 'outlined',
    openNewTab: false,
    isVideoTrigger: false,
    videoUrl: '',
    bgColor: '',
    textColor: '',
  },
  buttonLayout: 'side-by-side',

  // Social proof
  socialProofVisible: false,
  ratingVisible: false,
  ratingValue: 5,
  ratingCount: '2,400 reviews',
  userCountText: '50,000+ teams',
  avatarsVisible: false,
  avatarImages: [],

  // Visual
  visualType: 'none',
  visualUrl: '',
  visualDeviceFrame: 'none',
  visualPosition: 'right',
  visualWidthPercent: 50,

  // Typography
  headlineFontFamily: '',
  bodyFontFamily: '',

  // Animation
  entranceAnimation: 'none',
  scrollIndicatorVisible: false,
  shapeDivider: 'none',
  shapeDividerColor: '#ffffff',
};

// ─── Gradient helpers ─────────────────────────────────────────────────────────

const GRADIENT_PRESETS = [
  { label: 'Indigo → Cyan',    from: '#6366f1', to: '#0ea5e9' },
  { label: 'Purple → Pink',    from: '#7c3aed', to: '#ec4899' },
  { label: 'Blue → Teal',      from: '#2563eb', to: '#14b8a6' },
  { label: 'Orange → Rose',    from: '#f97316', to: '#e11d48' },
  { label: 'Emerald → Cyan',   from: '#10b981', to: '#06b6d4' },
  { label: 'Slate → Zinc',     from: '#1e293b', to: '#3f3f46' },
  { label: 'Violet → Amber',   from: '#8b5cf6', to: '#f59e0b' },
  { label: 'Sky → Indigo',     from: '#0284c7', to: '#4f46e5' },
];

function buildGradientCSS(from, to, type, angle) {
  const c1 = from || '#6366f1';
  const c2 = to   || '#0ea5e9';
  if (type === 'radial') return `radial-gradient(circle, ${c1}, ${c2})`;
  return `linear-gradient(${angle ?? 135}deg, ${c1}, ${c2})`;
}

// ─── Presets ──────────────────────────────────────────────────────────────────

const PRESETS = [
  {
    key: 'minimal',
    label: 'Minimal Centered',
    icon: 'bi-align-center',
    description: 'Clean white background, centered copy',
    config: {
      ...DEFAULT_HERO_CONFIG,
      layoutType: 'centered',
      textAlignment: 'center',
      bgType: 'color',
      bgColor: '#ffffff',
      headlineColor: '#111827',
      headlineFontSize: 'xl',
      subheadlineColor: '#6b7280',
      headlineText: 'Simple. Clean. Focused.',
      subheadlineText: "Everything you need, nothing you don't.",
      visualType: 'none',
      entranceAnimation: 'fade-in',
    },
  },
  {
    key: 'bold_split',
    label: 'Bold Split-Screen',
    icon: 'bi-layout-split',
    description: 'Dark gradient, visual on the right',
    config: {
      ...DEFAULT_HERO_CONFIG,
      layoutType: 'split-left',
      textAlignment: 'left',
      bgType: 'gradient',
      headlineColor: '#ffffff',
      headlineText: 'The platform teams love.',
      badgeVisible: true,
      badgeText: 'Now in v2.0',
      badgeStyle: 'pill',
      badgeColor: '#7c3aed',
      visualType: 'image',
      visualPosition: 'right',
      visualWidthPercent: 50,
      secondaryCTA: { ...DEFAULT_HERO_CONFIG.secondaryCTA, visible: true },
      entranceAnimation: 'slide-up',
    },
  },
  {
    key: 'dark_tech',
    label: 'Dark Tech',
    icon: 'bi-cpu',
    description: 'Dark background, split layout with phone frame visual',
    config: {
      ...DEFAULT_HERO_CONFIG,
      layoutType: 'split-left',
      textAlignment: 'left',
      bgType: 'color',
      bgColor: '#0f172a',
      headlineColor: '#f1f5f9',
      headlineFontSize: 'lg',
      subheadlineColor: '#94a3b8',
      badgeVisible: true,
      badgeText: 'Beta',
      badgeStyle: 'outlined',
      badgeColor: '#38bdf8',
      visualType: 'image',
      visualDeviceFrame: 'phone',
      visualPosition: 'right',
      visualWidthPercent: 40,
      secondaryCTA: { ...DEFAULT_HERO_CONFIG.secondaryCTA, visible: true, style: 'ghost' },
      entranceAnimation: 'slide-up',
      shapeDivider: 'none',
    },
  },
  {
    key: 'creative_agency',
    label: 'Creative Agency',
    icon: 'bi-brush',
    description: 'Bold gradient, left-aligned, diagonal divider',
    config: {
      ...DEFAULT_HERO_CONFIG,
      layoutType: 'left-aligned',
      textAlignment: 'left',
      bgType: 'gradient',
      headlineColor: '#ffffff',
      headlineFontSize: 'xl',
      subheadlineColor: '#cbd5e1',
      badgeVisible: false,
      visualType: 'none',
      secondaryCTA: { ...DEFAULT_HERO_CONFIG.secondaryCTA, visible: true, style: 'ghost' },
      shapeDivider: 'diagonal',
      shapeDividerColor: '#f8fafc',
      entranceAnimation: 'slide-up',
      paddingTop: 100,
      paddingBottom: 120,
    },
  },
];

// ─── Primitive controls ───────────────────────────────────────────────────────

function Toggle({ value, onChange }) {
  const { theme } = useTheme();
  return (
    <div
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      style={{
        width: 36, height: 20, borderRadius: 10,
        background: value ? theme.primary : '#cbd5e1',
        position: 'relative', cursor: 'pointer',
        transition: 'background 0.18s', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: 2,
        left: value ? 18 : 2, width: 16, height: 16,
        borderRadius: '50%', background: '#fff',
        transition: 'left 0.18s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
      }} />
    </div>
  );
}

function Seg({ value, onChange, options }) {
  const { theme } = useTheme();
  return (
    <div style={{
      display: 'flex', borderRadius: 6, overflow: 'hidden',
      border: '1px solid #e2e8f0', width: '100%',
    }}>
      {options.map((opt, i) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            title={opt.label}
            onClick={() => onChange(opt.value)}
            style={{
              flex: 1, padding: '5px 4px',
              border: 'none',
              borderLeft: i > 0 ? '1px solid #e2e8f0' : 'none',
              background: active ? theme.primary : '#f8fafc',
              color: active ? '#fff' : '#64748b',
              fontSize: 11, fontWeight: active ? 600 : 400,
              cursor: 'pointer', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
              transition: 'background 0.14s, color 0.14s',
            }}
          >
            {opt.icon && <i className={`bi ${opt.icon}`} style={{ fontSize: 11 }} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ColorField({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        type="color"
        value={value || '#000000'}
        onChange={e => onChange(e.target.value)}
        style={{
          width: 32, height: 28, padding: 2,
          border: '1px solid #e2e8f0', borderRadius: 4,
          cursor: 'pointer', flexShrink: 0,
        }}
      />
      <input
        className="form-control form-control-sm"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder="#000000"
        style={{ fontFamily: 'monospace', fontSize: 12 }}
      />
    </div>
  );
}

function UrlInput({ value, onChange, placeholder }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <input
        className="form-control form-control-sm"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'https://...'}
        style={{ flex: 1 }}
      />
      {value && (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#94a3b8', padding: '0 4px', flexShrink: 0 }}
          title="Open link"
          onClick={e => e.stopPropagation()}
        >
          <i className="bi bi-box-arrow-up-right" style={{ fontSize: 12 }} />
        </a>
      )}
    </div>
  );
}

function StringList({ items = [], onChange, placeholder = 'Add item…', maxItems }) {
  const { theme } = useTheme();
  const [draft, setDraft] = useState('');
  const canAdd = !maxItems || items.length < maxItems;

  const add = () => {
    if (!draft.trim()) return;
    onChange([...items, draft.trim()]);
    setDraft('');
  };

  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
          <input
            className="form-control form-control-sm"
            value={item}
            onChange={e => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
            style={{ flex: 1, fontSize: 12 }}
          />
          <button
            type="button"
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}
          >
            <i className="bi bi-x" style={{ fontSize: 15 }} />
          </button>
        </div>
      ))}
      {canAdd && (
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            className="form-control form-control-sm"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={placeholder}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            style={{ flex: 1, fontSize: 12 }}
          />
          <button
            type="button"
            onClick={add}
            disabled={!draft.trim()}
            style={{
              background: draft.trim() ? theme.primary : '#e2e8f0',
              color: draft.trim() ? '#fff' : '#94a3b8',
              border: 'none', borderRadius: 4,
              padding: '2px 10px', cursor: draft.trim() ? 'pointer' : 'default',
              fontSize: 14, flexShrink: 0,
            }}
          >
            <i className="bi bi-plus" />
          </button>
        </div>
      )}
      {maxItems && items.length >= maxItems && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Maximum {maxItems} items</div>
      )}
    </div>
  );
}

function NumInput({ value, onChange, min, max, step = 1, unit }) {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      <input
        type="number"
        className="form-control form-control-sm"
        value={value ?? ''}
        min={min} max={max} step={step}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: 76 }}
      />
      {unit && <span style={{ fontSize: 11, color: '#94a3b8' }}>{unit}</span>}
    </div>
  );
}

function RangeSlider({ value, onChange, min = 0, max = 100, step = 1, unit }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="range"
        className="form-range"
        value={value ?? min}
        min={min} max={max} step={step}
        onChange={e => onChange(Number(e.target.value))}
        style={{ flex: 1 }}
      />
      <span style={{ fontSize: 11, color: '#64748b', minWidth: 38, textAlign: 'right', flexShrink: 0 }}>
        {value ?? min}{unit || ''}
      </span>
    </div>
  );
}

// Label above → full-width control below
function Row({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      {children}
      {hint && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

// Label on left, control on right (toggles and short inputs)
function InlineRow({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{label}</div>
        {hint && <div style={{ fontSize: 10, color: '#94a3b8' }}>{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', margin: '12px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
      {label}
      <div style={{ flex: 1, height: 1, background: '#f1f5f9' }} />
    </div>
  );
}

function SubPanel({ children }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: 6, padding: '10px 10px 2px', marginBottom: 8 }}>
      {children}
    </div>
  );
}

function Accordion({ title, icon, children, open, onToggle, badge }) {
  const { theme } = useTheme();
  return (
    <div style={{ marginBottom: 3 }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 7,
          padding: '8px 10px',
          border: 'none',
          borderRadius: open ? '6px 6px 0 0' : 6,
          background: open ? theme.primary + '12' : '#f8fafc',
          color: open ? theme.primary : '#374151',
          fontWeight: 600, fontSize: 12, cursor: 'pointer',
          borderBottom: open ? `1px solid ${theme.primary}25` : '1px solid transparent',
          transition: 'background 0.14s',
        }}
      >
        {icon && <i className={`bi ${icon}`} style={{ fontSize: 13, flexShrink: 0 }} />}
        <span style={{ flex: 1, textAlign: 'left' }}>{title}</span>
        {badge && (
          <span style={{
            fontSize: 10, padding: '1px 6px', borderRadius: 10,
            background: theme.primary + '20', color: theme.primary, fontWeight: 600,
          }}>{badge}</span>
        )}
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: 10, flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{
          padding: '12px 10px 4px',
          border: `1px solid ${theme.primary}18`,
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          background: '#fff',
          marginBottom: 2,
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Google Fonts list ────────────────────────────────────────────────────────

const GOOGLE_FONTS = [
  // Sans-serif
  'Albert Sans', 'Archivo', 'Barlow', 'Barlow Condensed', 'Cabin', 'DM Sans',
  'Exo 2', 'Figtree', 'Fira Sans', 'IBM Plex Sans', 'Inter', 'Jost',
  'Josefin Sans', 'Lato', 'Lexend', 'Manrope', 'Montserrat', 'Mulish',
  'Noto Sans', 'Nunito', 'Open Sans', 'Outfit', 'Plus Jakarta Sans',
  'Poppins', 'Quicksand', 'Raleway', 'Roboto', 'Rubik', 'Sora',
  'Source Sans 3', 'Syne', 'Ubuntu', 'Urbanist', 'Work Sans',
  // Serif
  'Cormorant Garamond', 'Crimson Text', 'DM Serif Display', 'EB Garamond',
  'Fraunces', 'Libre Baskerville', 'Lora', 'Merriweather', 'Noto Serif',
  'Playfair Display', 'Source Serif 4', 'Spectral',
  // Display / Condensed
  'Anton', 'Bebas Neue', 'Boogaloo', 'Chakra Petch', 'Clash Display',
  'Kanit', 'Oswald', 'Righteous', 'Russo One', 'Saira', 'Space Grotesk',
  'Teko', 'Yanone Kaffeesatz',
  // Monospace
  'DM Mono', 'Fira Code', 'IBM Plex Mono', 'JetBrains Mono',
  'Roboto Mono', 'Source Code Pro', 'Space Mono',
  // Script / Handwriting
  'Caveat', 'Dancing Script', 'Great Vibes', 'Indie Flower',
  'Pacifico', 'Sacramento', 'Satisfy',
].sort((a, b) => a.localeCompare(b));

// ─── Font Picker ──────────────────────────────────────────────────────────────

function FontPicker({ value, onChange }) {
  const { theme } = useTheme();
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const containerRef        = useRef(null);
  const searchRef           = useRef(null);

  const filtered = GOOGLE_FONTS.filter(f =>
    f.toLowerCase().includes(search.toLowerCase())
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Auto-focus search when opening
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const select = (font) => { onChange(font); setOpen(false); setSearch(''); };
  const clear  = ()      => { onChange('');   setOpen(false); setSearch(''); };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6,
          background: '#fff', cursor: 'pointer', fontSize: 12, gap: 6,
          color: value ? '#1e293b' : '#9ca3af',
          outline: open ? `2px solid ${theme.primary}40` : 'none',
        }}
      >
        <span style={{ fontFamily: value ? `"${value}", sans-serif` : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'Default (inherit)'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {value && (
            <span
              role="button"
              onClick={e => { e.stopPropagation(); clear(); }}
              style={{ color: '#94a3b8', lineHeight: 1, padding: '0 2px', cursor: 'pointer' }}
              title="Clear font"
            >
              <i className="bi bi-x" style={{ fontSize: 13 }} />
            </span>
          )}
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
        </div>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 28px rgba(0,0,0,0.14)', overflow: 'hidden',
        }}>
          {/* Search bar */}
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ position: 'relative' }}>
              <i className="bi bi-search" style={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                fontSize: 11, color: '#94a3b8', pointerEvents: 'none',
              }} />
              <input
                ref={searchRef}
                className="form-control form-control-sm"
                placeholder="Search fonts…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setOpen(false); setSearch(''); }
                  if (e.key === 'Enter' && filtered.length > 0) select(filtered[0]);
                }}
                style={{ paddingLeft: 26, fontSize: 12 }}
              />
            </div>
          </div>

          {/* Font list */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {/* Clear / Default option */}
            <button
              type="button"
              onClick={clear}
              style={{
                width: '100%', padding: '7px 12px', border: 'none', borderBottom: '1px solid #f1f5f9',
                background: !value ? `${theme.primary}10` : '#fff',
                textAlign: 'left', fontSize: 11, color: '#94a3b8',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <i className="bi bi-x-circle" style={{ fontSize: 11 }} />
              Default (no custom font)
            </button>

            {filtered.length === 0 ? (
              <div style={{ padding: '14px 12px', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                No fonts match "<strong>{search}</strong>"
              </div>
            ) : (
              filtered.map(font => {
                const active = value === font;
                return (
                  <button
                    key={font}
                    type="button"
                    onClick={() => select(font)}
                    style={{
                      width: '100%', padding: '7px 12px', border: 'none',
                      borderBottom: '1px solid #f8fafc',
                      background: active ? `${theme.primary}10` : '#fff',
                      textAlign: 'left', fontSize: 12,
                      color: active ? theme.primary : '#374151',
                      fontWeight: active ? 700 : 400,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = '#fff'; }}
                  >
                    <span style={{ flexShrink: 0 }}>{font}</span>
                    <span style={{
                      fontFamily: `"${font}", sans-serif`, fontSize: 14, color: '#64748b',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      Aa Bb Cc
                    </span>
                    {active && <i className="bi bi-check2" style={{ fontSize: 12, color: theme.primary, flexShrink: 0 }} />}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer hint */}
          <div style={{ padding: '6px 10px', borderTop: '1px solid #f1f5f9', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
            {filtered.length} font{filtered.length !== 1 ? 's' : ''} • loads automatically from Google Fonts
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CTA Link Config ──────────────────────────────────────────────────────────

function CTALinkConfig({ cta, onChange, allSections, workspacePages }) {
  const linkType = cta.linkType || 'url';

  const sections = (allSections || []).filter(s => s.section_type);
  const selectedSection = sections.find(s => String(s.id || s._clientId) === String(cta.targetSectionId));
  const sectionLabel = selectedSection
    ? (selectedSection.sectionName || selectedSection.section_type?.replace(/_/g, ' '))
    : '';

  const pub  = (workspacePages || []).filter(p => (p.visibility || 'public') === 'public');
  const priv = (workspacePages || []).filter(p => (p.visibility || 'public') === 'private');
  const selectedPage = (workspacePages || []).find(p => String(p.id) === String(cta.targetPageId));

  const [sectionOpen, setSectionOpen] = useState(false);
  const [pageOpen, setPageOpen]       = useState(false);

  return (
    <>
      <Row label="Link To">
        <Seg value={linkType} onChange={v => onChange({ linkType: v })} options={[
          { value: 'url',     label: 'URL' },
          { value: 'section', label: 'Section' },
          { value: 'page',    label: 'Page' },
        ]} />
      </Row>

      {linkType === 'url' && (
        <Row label="URL">
          <UrlInput value={cta.url} onChange={v => onChange({ url: v })} placeholder="/sign-up or https://..." />
        </Row>
      )}

      {linkType === 'section' && (
        <Row label="Target Section">
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => setSectionOpen(o => !o)}
              style={{
                border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px',
                fontSize: 12, cursor: 'pointer', background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              {cta.targetSectionId && sectionLabel ? (
                <span><i className="bi bi-hash" style={{ color: '#7c3aed', marginRight: 4 }} />{sectionLabel}</span>
              ) : (
                <span style={{ color: '#94a3b8' }}>Select a section…</span>
              )}
              <i className={`bi bi-chevron-${sectionOpen ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
            </div>
            {sectionOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 2,
              }}>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {sections.length === 0 ? (
                    <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12 }}>No sections on this page</div>
                  ) : sections.map((s, i) => {
                    const id = String(s.id || s._clientId || i);
                    const name = s.sectionName || s.section_type?.replace(/_/g, ' ') || 'Section';
                    const active = String(cta.targetSectionId) === id;
                    return (
                      <div key={id}
                        onClick={() => { onChange({ targetSectionId: id }); setSectionOpen(false); }}
                        style={{
                          padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                          background: active ? '#f5f3ff' : 'transparent',
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = active ? '#f5f3ff' : '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = active ? '#f5f3ff' : 'transparent'}
                      >
                        <i className="bi bi-hash" style={{ color: '#7c3aed', fontSize: 11 }} />
                        <span style={{ fontWeight: 500 }}>{name}</span>
                        <span style={{ color: '#94a3b8', marginLeft: 'auto', fontSize: 10 }}>
                          {s.section_type?.replace(/_/g, ' ')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Row>
      )}

      {linkType === 'page' && (
        <Row label="Target Page">
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => setPageOpen(o => !o)}
              style={{
                border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px',
                fontSize: 12, cursor: 'pointer', background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              {selectedPage ? (
                <span>
                  <i className={`bi ${selectedPage.visibility === 'private' ? 'bi-lock-fill' : 'bi-globe'}`}
                    style={{ color: selectedPage.visibility === 'private' ? '#475569' : '#16a34a', fontSize: 10, marginRight: 4 }} />
                  {selectedPage.name}
                  <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 10 }}>/{selectedPage.slug}</span>
                </span>
              ) : (
                <span style={{ color: '#94a3b8' }}>Select a page…</span>
              )}
              <i className={`bi bi-chevron-${pageOpen ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
            </div>
            {pageOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 2,
              }}>
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {pub.length > 0 && (
                    <>
                      <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Public Pages
                      </div>
                      {pub.map(p => {
                        const active = String(p.id) === String(cta.targetPageId);
                        return (
                          <div key={p.id}
                            onClick={() => { onChange({ targetPageId: String(p.id), targetPageSlug: p.slug || '' }); setPageOpen(false); }}
                            style={{
                              padding: '7px 12px', cursor: 'pointer', fontSize: 12,
                              background: active ? '#f5f3ff' : 'transparent',
                              display: 'flex', alignItems: 'center', gap: 6,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = active ? '#f5f3ff' : '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = active ? '#f5f3ff' : 'transparent'}
                          >
                            <i className="bi bi-globe" style={{ color: '#16a34a', fontSize: 10 }} />
                            <span style={{ fontWeight: 500 }}>{p.name}</span>
                            <span style={{ color: '#94a3b8', marginLeft: 'auto', fontSize: 10 }}>/{p.slug}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                  {priv.length > 0 && (
                    <>
                      <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Private Pages
                      </div>
                      {priv.map(p => {
                        const active = String(p.id) === String(cta.targetPageId);
                        return (
                          <div key={p.id}
                            onClick={() => { onChange({ targetPageId: String(p.id), targetPageSlug: p.slug || '' }); setPageOpen(false); }}
                            style={{
                              padding: '7px 12px', cursor: 'pointer', fontSize: 12,
                              background: active ? '#f5f3ff' : 'transparent',
                              display: 'flex', alignItems: 'center', gap: 6,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = active ? '#f5f3ff' : '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = active ? '#f5f3ff' : 'transparent'}
                          >
                            <i className="bi bi-lock-fill" style={{ color: '#475569', fontSize: 10 }} />
                            <span style={{ fontWeight: 500 }}>{p.name}</span>
                            <span style={{ color: '#94a3b8', marginLeft: 'auto', fontSize: 10 }}>/{p.slug}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                  {pub.length === 0 && priv.length === 0 && (
                    <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12 }}>No pages in workspace</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Row>
      )}
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HeroEditor({ section, dispatch, appId, allSections }) {
  const { theme } = useTheme();
  const { data: pagesData } = usePages(appId);
  const workspacePages = pagesData?.pages || pagesData || [];
  const cfg = { ...DEFAULT_HERO_CONFIG, ...section.section_config };
  // Ensure nested objects are also properly defaulted
  const primaryCTA = { ...DEFAULT_HERO_CONFIG.primaryCTA, ...(section.section_config?.primaryCTA || {}) };
  const secondaryCTA = { ...DEFAULT_HERO_CONFIG.secondaryCTA, ...(section.section_config?.secondaryCTA || {}) };

  const [activeTab, setActiveTab] = useState('content');
  const [showPresets, setShowPresets] = useState(false);

  const HERO_TABS = [
    { key: 'content',    label: 'Content',    icon: 'bi-pencil' },
    { key: 'layout',     label: 'Layout',     icon: 'bi-layout-three-columns' },
    { key: 'background', label: 'Background', icon: 'bi-palette' },
    { key: 'buttons',    label: 'Buttons',    icon: 'bi-cursor-fill' },
    { key: 'media',      label: 'Media',      icon: 'bi-image' },
    { key: 'effects',    label: 'Effects',    icon: 'bi-magic' },
  ];

  const set = useCallback((patch) => {
    dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });
  }, [dispatch, section._clientId]);

  const setPrimary = (patch) => set({ primaryCTA: { ...primaryCTA, ...patch } });
  const setSecondary = (patch) => set({ secondaryCTA: { ...secondaryCTA, ...patch } });

  const applyPreset = (preset) => {
    // Preserve the user's text/content values — only apply structural/visual settings from preset
    const current        = { ...DEFAULT_HERO_CONFIG, ...section.section_config };
    const curPrimary     = { ...DEFAULT_HERO_CONFIG.primaryCTA,   ...(section.section_config?.primaryCTA   || {}) };
    const curSecondary   = { ...DEFAULT_HERO_CONFIG.secondaryCTA, ...(section.section_config?.secondaryCTA || {}) };
    const merged = {
      ...preset.config,
      // Preserve all user-authored text and content fields
      headlineText:            current.headlineText,
      headlineHighlightWord:   current.headlineHighlightWord,
      headlineHighlightColor:  current.headlineHighlightColor,
      headlineTypewriter:      current.headlineTypewriter,
      headlineTypewriterWords: current.headlineTypewriterWords,
      headlineFontFamily:      current.headlineFontFamily,
      subheadlineText:         current.subheadlineText,
      bodyFontFamily:          current.bodyFontFamily,
      badgeText:               current.badgeText,
      badgeLink:               current.badgeLink,
      ratingValue:             current.ratingValue,
      ratingCount:             current.ratingCount,
      userCountText:           current.userCountText,
      avatarImages:            current.avatarImages,
      visualUrl:               current.visualUrl,
      paddingTop:              current.paddingTop,
      paddingBottom:           current.paddingBottom,
      primaryCTA: {
        ...(preset.config.primaryCTA || DEFAULT_HERO_CONFIG.primaryCTA),
        label:           curPrimary.label,
        linkType:        curPrimary.linkType,
        url:             curPrimary.url,
        targetSectionId: curPrimary.targetSectionId,
        targetPageId:    curPrimary.targetPageId,
        targetPageSlug:  curPrimary.targetPageSlug,
        icon:            curPrimary.icon,
        iconPosition:    curPrimary.iconPosition,
        openNewTab:      curPrimary.openNewTab,
        bgColor:         curPrimary.bgColor,
        textColor:       curPrimary.textColor,
      },
      secondaryCTA: {
        ...(preset.config.secondaryCTA || DEFAULT_HERO_CONFIG.secondaryCTA),
        visible:         curSecondary.visible,
        label:           curSecondary.label,
        linkType:        curSecondary.linkType,
        url:             curSecondary.url,
        targetSectionId: curSecondary.targetSectionId,
        targetPageId:    curSecondary.targetPageId,
        targetPageSlug:  curSecondary.targetPageSlug,
        icon:            curSecondary.icon,
        openNewTab:      curSecondary.openNewTab,
        videoUrl:        curSecondary.videoUrl,
        isVideoTrigger:  curSecondary.isVideoTrigger,
        bgColor:         curSecondary.bgColor,
        textColor:       curSecondary.textColor,
      },
    };
    dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch: merged });
    setShowPresets(false);
  };

  const resetToDefault = () => {
    const minimalPreset = PRESETS.find(p => p.key === 'minimal');
    dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch: minimalPreset.config });
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>

      {/* ── Top bar: Presets + Reset ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, position: 'relative' }}>
        <button
          type="button"
          onClick={() => setShowPresets(p => !p)}
          style={{
            flex: 1, padding: '6px 10px',
            border: `1px solid ${theme.primary}`,
            borderRadius: 6,
            background: showPresets ? theme.primary : '#fff',
            color: showPresets ? '#fff' : theme.primary,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}
        >
          <i className="bi bi-stars" />
          Presets
          <i className={`bi bi-chevron-${showPresets ? 'up' : 'down'}`} style={{ fontSize: 10 }} />
        </button>
        <button
          type="button"
          onClick={resetToDefault}
          title="Reset all fields to defaults"
          style={{
            padding: '6px 10px', border: '1px solid #e2e8f0',
            borderRadius: 6, background: '#fff', color: '#64748b',
            fontSize: 12, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          <i className="bi bi-arrow-counterclockwise" />
          Reset
        </button>

        {showPresets && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
            background: '#fff', border: '1px solid #e2e8f0',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
            overflow: 'hidden', marginTop: 4,
          }}>
            {PRESETS.map((preset, i) => (
              <button
                key={preset.key}
                type="button"
                onClick={() => applyPreset(preset)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', border: 'none', background: '#fff',
                  cursor: 'pointer', textAlign: 'left',
                  borderBottom: i < PRESETS.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = theme.primary + '0f')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <i className={`bi ${preset.icon}`} style={{ fontSize: 18, color: theme.primary, width: 22, textAlign: 'center', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{preset.label}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{preset.description}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Tab bar ── */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0',
        marginBottom: 14, overflowX: 'auto', flexShrink: 0,
      }}>
        {HERO_TABS.map(tab => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '9px 14px',
            border: 'none',
            borderBottom: activeTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
            marginBottom: -2,
            background: 'transparent',
            color: activeTab === tab.key ? theme.primary : '#64748b',
            fontWeight: activeTab === tab.key ? 700 : 400,
            fontSize: 11,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'color 0.15s',
          }}>
            <i className={`bi ${tab.icon}`} style={{ fontSize: 12 }} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Layout tab ── */}
      {activeTab === 'layout' && (
        <>
        <Row label="Layout Type">
          <Seg value={cfg.layoutType} onChange={v => {
            const isSplitNow = cfg.layoutType === 'split-left' || cfg.layoutType === 'split-right';
            const isSplitNext = v === 'split-left' || v === 'split-right';
            const patch = { layoutType: v };
            if (v === 'centered') {
              patch.textAlignment = 'center';
            } else {
              patch.textAlignment = 'left';
            }
            if (isSplitNow && !isSplitNext && cfg.visualPosition !== 'below') {
              patch.visualPosition = 'below';
            }
            if (!isSplitNow && isSplitNext && cfg.visualPosition === 'below') {
              patch.visualPosition = 'right';
            }
            set(patch);
          }} options={[
            { value: 'centered',     label: 'Center',  icon: 'bi-align-center' },
            { value: 'left-aligned', label: 'Left',    icon: 'bi-align-start' },
            { value: 'split-left',   label: 'Split L' },
          ]} />
        </Row>

        <Row label="Content Max Width">
          <Seg value={cfg.contentMaxWidth} onChange={v => set({ contentMaxWidth: v })} options={[
            { value: 'narrow', label: 'Narrow' },
            { value: 'medium', label: 'Medium' },
            { value: 'wide',   label: 'Wide' },
            { value: 'full',   label: 'Full' },
          ]} />
        </Row>

        <Row label="Text Alignment">
          <Seg value={cfg.textAlignment} onChange={v => set({ textAlignment: v })} options={[
            { value: 'left',   icon: 'bi-text-left',   label: 'Left' },
            { value: 'center', icon: 'bi-text-center', label: 'Center' },
            { value: 'right',  icon: 'bi-text-right',  label: 'Right' },
          ]} />
        </Row>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Row label="Padding Top">
            <NumInput value={cfg.paddingTop} onChange={v => set({ paddingTop: v })} min={0} max={320} step={4} unit="px" />
          </Row>
          <Row label="Padding Bottom">
            <NumInput value={cfg.paddingBottom} onChange={v => set({ paddingBottom: v })} min={0} max={320} step={4} unit="px" />
          </Row>
        </div>
        </>
      )}

      {/* ── Background tab ── */}
      {activeTab === 'background' && (
        <>

        <Row label="Type">
          <Seg value={cfg.bgType} onChange={v => set({ bgType: v })} options={[
            { value: 'gradient', label: 'Gradient' },
            { value: 'color',    label: 'Color' },
            { value: 'image',    label: 'Image' },
          ]} />
        </Row>

        {cfg.bgType === 'gradient' && (
          <>
            <Row label="Colors">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1 }}><ColorField value={cfg.bgGradientFrom} onChange={v => set({ bgGradientFrom: v })} /></div>
                <i className="bi bi-arrow-right" style={{ color: '#94a3b8', fontSize: 12, flexShrink: 0 }} />
                <div style={{ flex: 1 }}><ColorField value={cfg.bgGradientTo} onChange={v => set({ bgGradientTo: v })} /></div>
              </div>
            </Row>
            <Row label="Gradient Type">
              <Seg value={cfg.bgGradientType} onChange={v => set({ bgGradientType: v })} options={[
                { value: 'linear', label: 'Linear' },
                { value: 'radial', label: 'Radial' },
              ]} />
            </Row>
            {cfg.bgGradientType !== 'radial' && (
              <Row label="Direction">
                <Seg value={String(cfg.bgGradientAngle ?? 135)} onChange={v => set({ bgGradientAngle: Number(v) })} options={[
                  { value: '0',   label: '↑' },
                  { value: '45',  label: '↗' },
                  { value: '90',  label: '→' },
                  { value: '135', label: '↘' },
                  { value: '180', label: '↓' },
                  { value: '225', label: '↙' },
                  { value: '270', label: '←' },
                  { value: '315', label: '↖' },
                ]} />
              </Row>
            )}
            {cfg.bgGradientType !== 'radial' && (
              <Row label={`Angle — ${cfg.bgGradientAngle ?? 135}°`}>
                <RangeSlider value={cfg.bgGradientAngle ?? 135} onChange={v => set({ bgGradientAngle: v })} min={0} max={360} unit="°" />
              </Row>
            )}
          </>
        )}

        {cfg.bgType === 'color' && (
          <Row label="Color">
            <ColorField value={cfg.bgColor} onChange={v => set({ bgColor: v })} />
          </Row>
        )}

        {cfg.bgType === 'image' && (
          <>
            <Row label="Image URL">
              <UrlInput value={cfg.bgImageUrl} onChange={v => set({ bgImageUrl: v })} placeholder="https://..." />
            </Row>
            <Row label="Position">
              <Seg value={cfg.bgImagePosition} onChange={v => set({ bgImagePosition: v })} options={[
                { value: 'center', label: 'Center' },
                { value: 'top',    label: 'Top' },
                { value: 'bottom', label: 'Bottom' },
                { value: 'left',   label: 'Left' },
              ]} />
            </Row>
            <Row label="Size">
              <Seg value={cfg.bgImageSize} onChange={v => set({ bgImageSize: v })} options={[
                { value: 'cover',   label: 'Cover' },
                { value: 'contain', label: 'Contain' },
                { value: 'auto',    label: 'Auto' },
              ]} />
            </Row>
          </>
        )}

        {cfg.bgType === 'image' && (
          <>
            <SectionDivider label="OVERLAY" />
            <Row label={`Overlay — ${cfg.bgOverlayOpacity}%`}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ColorField value={cfg.bgOverlayColor} onChange={v => set({ bgOverlayColor: v })} />
                <div style={{ flex: 1 }}>
                  <RangeSlider value={cfg.bgOverlayOpacity} onChange={v => set({ bgOverlayOpacity: v })} min={0} max={100} unit="%" />
                </div>
              </div>
            </Row>
          </>
        )}
        </>
      )}

      {/* ── Content tab (Badge + Headline + Subheadline) ── */}
      {activeTab === 'content' && (
        <>
        <SectionDivider label="BADGE" />
        <InlineRow label="Show Badge">
          <Toggle value={cfg.badgeVisible} onChange={v => set({ badgeVisible: v })} />
        </InlineRow>

        {cfg.badgeVisible && (
          <>
            <Row label="Text">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <input className="form-control form-control-sm" value={cfg.badgeText} onChange={e => set({ badgeText: e.target.value })} />
                </div>
                <ColorField value={cfg.badgeColor} onChange={v => set({ badgeColor: v })} />
              </div>
            </Row>
            <Row label="Style">
              <Seg value={cfg.badgeStyle} onChange={v => set({ badgeStyle: v })} options={[
                { value: 'filled',   label: 'Filled' },
                { value: 'outlined', label: 'Outlined' },
                { value: 'pill',     label: 'Pill' },
              ]} />
            </Row>
          </>
        )}
        <SectionDivider label="HEADLINE" />
        <Row label="Headline Text" hint="Use \n for a manual line break">
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={cfg.headlineText}
            onChange={e => set({ headlineText: e.target.value })}
            style={{ fontSize: 12, resize: 'vertical' }}
          />
        </Row>

        <Row label="Font Size">
          <Seg value={cfg.headlineFontSize} onChange={v => set({ headlineFontSize: v })} options={[
            { value: 'sm', label: 'SM' },
            { value: 'md', label: 'MD' },
            { value: 'lg', label: 'LG' },
            { value: 'xl', label: 'XL' },
          ]} />
        </Row>

        <Row label="Color">
          <ColorField value={cfg.headlineColor} onChange={v => set({ headlineColor: v })} />
        </Row>

        <Row label="Font">
          <FontPicker value={cfg.headlineFontFamily} onChange={v => set({ headlineFontFamily: v })} />
        </Row>

        <SectionDivider label="HIGHLIGHT" />

        <Row label="Word to Highlight" hint="Exact word from your headline text">
          <input
            className="form-control form-control-sm"
            placeholder="e.g. great"
            value={cfg.headlineHighlightWord}
            onChange={e => set({ headlineHighlightWord: e.target.value })}
          />
        </Row>

        {cfg.headlineHighlightWord && (
          <>
            <Row label="Highlight Style">
              <Seg value={cfg.headlineHighlightStyle} onChange={v => set({ headlineHighlightStyle: v })} options={[
                { value: 'color',     label: 'Color' },
                { value: 'underline', label: 'Underline' },
                { value: 'mark',      label: 'Mark' },
              ]} />
            </Row>
            {cfg.headlineHighlightStyle !== 'underline' && (
              <Row label="Highlight Color">
                <ColorField value={cfg.headlineHighlightColor} onChange={v => set({ headlineHighlightColor: v })} />
              </Row>
            )}
          </>
        )}

        <SectionDivider label="SUBHEADLINE" />
        <InlineRow label="Show Subheadline">
          <Toggle value={cfg.subheadlineVisible} onChange={v => set({ subheadlineVisible: v })} />
        </InlineRow>

        {cfg.subheadlineVisible && (
          <>
            <Row label="Subheadline Text">
              <textarea
                className="form-control form-control-sm"
                rows={2}
                value={cfg.subheadlineText}
                onChange={e => set({ subheadlineText: e.target.value })}
                style={{ fontSize: 12, resize: 'vertical' }}
              />
            </Row>
            <Row label="Font Size">
              <Seg value={cfg.subheadlineFontSize} onChange={v => set({ subheadlineFontSize: v })} options={[
                { value: 'sm', label: 'SM' },
                { value: 'md', label: 'MD' },
                { value: 'lg', label: 'LG' },
              ]} />
            </Row>
            <Row label="Color">
              <ColorField value={cfg.subheadlineColor} onChange={v => set({ subheadlineColor: v })} />
            </Row>
            <Row label="Font">
              <FontPicker value={cfg.bodyFontFamily} onChange={v => set({ bodyFontFamily: v })} />
            </Row>
          </>
        )}
        </>
      )}

      {/* ── Buttons tab ── */}
      {activeTab === 'buttons' && (
        <>
        <Row label="Button Layout">
          <Seg value={cfg.buttonLayout} onChange={v => set({ buttonLayout: v })} options={[
            { value: 'side-by-side', label: 'Side by Side' },
            { value: 'stacked',      label: 'Stacked' },
            { value: 'full-width',   label: 'Full Width' },
          ]} />
        </Row>

        {/* Primary CTA */}
        <SubPanel>
          <SectionDivider label="PRIMARY BUTTON" />
          <Row label="Label">
            <input className="form-control form-control-sm" value={primaryCTA.label} onChange={e => setPrimary({ label: e.target.value })} />
          </Row>
          <CTALinkConfig cta={primaryCTA} onChange={setPrimary} allSections={allSections} workspacePages={workspacePages} />
          <Row label="Style">
            <Seg value={primaryCTA.style} onChange={v => setPrimary({ style: v })} options={[
              { value: 'filled',   label: 'Filled' },
              { value: 'outlined', label: 'Outlined' },
              { value: 'ghost',    label: 'Ghost' },
            ]} />
          </Row>
          <Row label="Colors">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3 }}>Background</div>
                <ColorField value={primaryCTA.bgColor} onChange={v => setPrimary({ bgColor: v })} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3 }}>Text</div>
                <ColorField value={primaryCTA.textColor} onChange={v => setPrimary({ textColor: v })} />
              </div>
            </div>
          </Row>
          <Row label="Icon" hint="Bootstrap Icons class (e.g. bi-arrow-right)">
            <input className="form-control form-control-sm" value={primaryCTA.icon} onChange={e => setPrimary({ icon: e.target.value })} placeholder="bi-arrow-right" />
          </Row>
          {primaryCTA.icon && (
            <Row label="Icon Position">
              <Seg value={primaryCTA.iconPosition} onChange={v => setPrimary({ iconPosition: v })} options={[
                { value: 'left',  label: 'Left' },
                { value: 'right', label: 'Right' },
              ]} />
            </Row>
          )}
          {(primaryCTA.linkType || 'url') === 'url' && (
            <InlineRow label="Open in New Tab">
              <Toggle value={primaryCTA.openNewTab} onChange={v => setPrimary({ openNewTab: v })} />
            </InlineRow>
          )}
        </SubPanel>

        {/* Secondary CTA */}
        <SubPanel>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <SectionDivider label="SECONDARY BUTTON" />
            <Toggle value={secondaryCTA.visible} onChange={v => setSecondary({ visible: v })} />
          </div>
          {secondaryCTA.visible && (
            <>
              <Row label="Label">
                <input className="form-control form-control-sm" value={secondaryCTA.label} onChange={e => setSecondary({ label: e.target.value })} />
              </Row>
              <Row label="Style">
                <Seg value={secondaryCTA.style} onChange={v => setSecondary({ style: v })} options={[
                  { value: 'outlined', label: 'Outlined' },
                  { value: 'ghost',    label: 'Ghost' },
                  { value: 'link',     label: 'Link' },
                ]} />
              </Row>
              <Row label="Colors">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3 }}>Background</div>
                    <ColorField value={secondaryCTA.bgColor} onChange={v => setSecondary({ bgColor: v })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3 }}>Text</div>
                    <ColorField value={secondaryCTA.textColor} onChange={v => setSecondary({ textColor: v })} />
                  </div>
                </div>
              </Row>
              <InlineRow label="Video Trigger" hint="Opens a video popup on click">
                <Toggle value={secondaryCTA.isVideoTrigger} onChange={v => setSecondary({ isVideoTrigger: v })} />
              </InlineRow>
              {secondaryCTA.isVideoTrigger ? (
                <Row label="Video URL">
                  <UrlInput value={secondaryCTA.videoUrl} onChange={v => setSecondary({ videoUrl: v })} placeholder="https://youtube.com/..." />
                </Row>
              ) : (
                <CTALinkConfig cta={secondaryCTA} onChange={setSecondary} allSections={allSections} workspacePages={workspacePages} />
              )}
              {(secondaryCTA.linkType || 'url') === 'url' && !secondaryCTA.isVideoTrigger && (
                <InlineRow label="Open in New Tab">
                  <Toggle value={secondaryCTA.openNewTab} onChange={v => setSecondary({ openNewTab: v })} />
                </InlineRow>
              )}
            </>
          )}
        </SubPanel>
        </>
      )}

      {/* ── Media tab (Social Proof + Hero Visual) ── */}
      {activeTab === 'media' && (
        <>
        <SectionDivider label="SOCIAL PROOF" />
        <InlineRow label="Show Social Proof">
          <Toggle value={cfg.socialProofVisible} onChange={v => set({ socialProofVisible: v })} />
        </InlineRow>

        {cfg.socialProofVisible && (
          <>
            {/* Star rating */}
            <SubPanel>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em' }}>STAR RATING</span>
                <Toggle value={cfg.ratingVisible} onChange={v => set({ ratingVisible: v })} />
              </div>
              {cfg.ratingVisible && (
                <>
                  <Row label={`Rating — ${cfg.ratingValue} / 5`}>
                    <RangeSlider value={cfg.ratingValue} onChange={v => set({ ratingValue: v })} min={1} max={5} step={0.5} />
                  </Row>
                  <Row label="Review Count Text">
                    <input className="form-control form-control-sm" value={cfg.ratingCount} onChange={e => set({ ratingCount: e.target.value })} placeholder="2,400 reviews" />
                  </Row>
                </>
              )}
            </SubPanel>

            <Row label="User Count Text">
              <input className="form-control form-control-sm" value={cfg.userCountText} onChange={e => set({ userCountText: e.target.value })} placeholder="50,000+ teams" />
            </Row>

            {/* Avatar stack */}
            <SubPanel>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em' }}>AVATAR STACK</span>
                <Toggle value={cfg.avatarsVisible} onChange={v => set({ avatarsVisible: v })} />
              </div>
              {cfg.avatarsVisible && (
                <Row label="Avatar URLs" hint="Up to 5 images">
                  <StringList items={cfg.avatarImages} onChange={v => set({ avatarImages: v })} placeholder="https://... (avatar URL)" maxItems={5} />
                </Row>
              )}
            </SubPanel>

          </>
        )}
        <SectionDivider label="HERO VISUAL" />
        <Row label="Visual Type">
          <Seg value={cfg.visualType} onChange={v => set({ visualType: v })} options={[
            { value: 'none',       label: 'None' },
            { value: 'image',      label: 'Image' },
            { value: 'video',      label: 'Video' },
            { value: 'screenshot', label: 'Screenshot' },
          ]} />
        </Row>

        {cfg.visualType !== 'none' && (
          <>
            <Row label="URL">
              <UrlInput value={cfg.visualUrl} onChange={v => set({ visualUrl: v })} placeholder="https://..." />
            </Row>

            <Row label="Device Frame">
              <Seg value={cfg.visualDeviceFrame} onChange={v => set({ visualDeviceFrame: v })} options={[
                { value: 'none',    label: 'None' },
                { value: 'browser', label: 'Browser' },
                { value: 'phone',   label: 'Phone' },
                { value: 'laptop',  label: 'Laptop' },
              ]} />
            </Row>

            <Row label="Position">
              <Seg value={cfg.visualPosition} onChange={v => set({ visualPosition: v })} options={[
                { value: 'right',      label: 'Right' },
                { value: 'left',       label: 'Left' },
                { value: 'below',      label: 'Below' },
                { value: 'background', label: 'BG' },
              ]} />
            </Row>

            <Row label={`Width — ${cfg.visualWidthPercent}%`}>
              <RangeSlider value={cfg.visualWidthPercent} onChange={v => set({ visualWidthPercent: v })} min={20} max={80} unit="%" />
            </Row>
          </>
        )}
        </>
      )}

      {/* ── Effects tab ── */}
      {activeTab === 'effects' && (
        <>
        <Row label="Entrance Animation">
          <Seg value={cfg.entranceAnimation} onChange={v => set({ entranceAnimation: v })} options={[
            { value: 'none',     label: 'None' },
            { value: 'fade-in',  label: 'Fade' },
            { value: 'slide-up', label: 'Slide Up' },
            { value: 'zoom-in',  label: 'Zoom' },
          ]} />
        </Row>

        <InlineRow label="Scroll Indicator" hint="Animated arrow at the bottom of the section">
          <Toggle value={cfg.scrollIndicatorVisible} onChange={v => set({ scrollIndicatorVisible: v })} />
        </InlineRow>

        <SectionDivider label="SHAPE DIVIDER" />

        <Row label="Divider Style">
          <Seg value={cfg.shapeDivider} onChange={v => set({ shapeDivider: v })} options={[
            { value: 'none',     label: 'None' },
            { value: 'wave',     label: 'Wave' },
            { value: 'diagonal', label: 'Diagonal' },
            { value: 'curve',    label: 'Curve' },
          ]} />
        </Row>

        {cfg.shapeDivider !== 'none' && (
          <Row label="Divider Color" hint="Usually matches the background of the next section">
            <ColorField value={cfg.shapeDividerColor} onChange={v => set({ shapeDividerColor: v })} />
          </Row>
        )}
        </>
      )}

    </div>
  );
}
